import React, { useEffect, useRef, useState } from 'react';
import { api } from '../services/api';
import AIProcessing from '../components/AIProcessing';
import AIComplete from '../components/AIComplete';
import { useCopyContext } from '../context/CopyContext';

type AgentKey =
  | 'lead-score'
  | 'competitor-analysis'
  | 'trend-hijack'
  | 'email-writer'
  | 'ad-copy'
  | 'seo-content'
  | 'whatsapp-gen'
  | 'strategy'
  | 'sleep-mode'
  | 'story-arc'
  | 'content-recycler'
  | 'brand-voice'
  | 'review-response'
  | 'lead-resurrection'
  | 'nri-content'
  | 'case-study'
  | 'festive-campaign'
  | 'journey-map';

const agents = [
  { key: 'lead-score', icon: '🎯', name: 'Lead Scoring', desc: 'Score leads 0-100' },
  { key: 'competitor-analysis', icon: '🕵️', name: 'Competitor Analysis', desc: 'Analyze competitors' },
  { key: 'trend-hijack', icon: '🔥', name: 'Trend Hijacker', desc: 'Viral trend content' },
  { key: 'email-writer', icon: '📧', name: 'Email Writer', desc: 'High-convert emails' },
  { key: 'ad-copy', icon: '📊', name: 'Ad Copy', desc: 'Facebook/Google ads' },
  { key: 'seo-content', icon: '🔍', name: 'SEO Writer', desc: 'Rank on Google' },
  { key: 'whatsapp-gen', icon: '💬', name: 'WhatsApp Gen', desc: 'Broadcast messages' },
  { key: 'strategy', icon: '🧠', name: 'Strategy Advisor', desc: 'Business strategy' },
  { key: 'sleep-mode', icon: '🌙', name: 'Sleep Mode', desc: 'Night visitor follow-up' },
  { key: 'story-arc', icon: '📖', name: 'Story Arc', desc: '90-day brand narrative' },
  { key: 'content-recycler', icon: '♻️', name: 'Content Recycler', desc: '1 content → 15 formats' },
  { key: 'brand-voice', icon: '🎤', name: 'Brand Voice', desc: 'Consistent brand tone' },
  { key: 'review-response', icon: '⭐', name: 'Review Response', desc: 'Handle reviews smartly' },
  { key: 'lead-resurrection', icon: '💀', name: 'Lead Resurrection', desc: 'Revive dead leads' },
  { key: 'nri-content', icon: '🌍', name: 'NRI Content', desc: 'Target NRI investors' },
  { key: 'case-study', icon: '📝', name: 'Case Study', desc: 'Success story generator' },
  { key: 'festive-campaign', icon: '🎉', name: 'Festive Campaign', desc: 'Festival marketing plan' },
  { key: 'journey-map', icon: '🗺️', name: 'Journey Mapper', desc: 'Customer path optimizer' },
] as const;

function PillGroup({
  options,
  value,
  onChange,
}: {
  options: string[];
  value: string;
  onChange: (v: string) => void;
}) {
  return (
    <div className="flex flex-wrap gap-2">
      {options.map((o) => (
        <button
          key={o}
          type="button"
          className="odc-pill-tab"
          style={
            value === o
              ? { borderColor: '#BE5103', background: 'rgba(190,81,3,0.16)', color: 'var(--text)' }
              : undefined
          }
          onClick={() => onChange(o)}
        >
          {o}
        </button>
      ))}
    </div>
  );
}

function ResultCard({ children }: { children: React.ReactNode }) {
  return (
    <section className="odc-card mt-6 !p-7" style={{ borderColor: 'rgba(190,81,3,0.4)' }}>
      {children}
    </section>
  );
}

export function AgentOpsPage() {
  const { copyText } = useCopyContext();
  const [activeAgent, setActiveAgent] = useState<AgentKey>('lead-score');
  const [loading, setLoading] = useState(false);
  const [showComplete, setShowComplete] = useState(false);
  const [result, setResult] = useState<any>(null);
  const [error, setError] = useState('');

  const [leadName, setLeadName] = useState('');
  const [mobile, setMobile] = useState('');
  const [source, setSource] = useState('WhatsApp');
  const [message, setMessage] = useState('');
  const [budget, setBudget] = useState('Not mentioned');
  const [location, setLocation] = useState('');
  const [interest, setInterest] = useState('');

  const [competitorName, setCompetitorName] = useState('');
  const [competitorInfo, setCompetitorInfo] = useState('');
  const [ourStrengths, setOurStrengths] = useState('');

  const [trendTopic, setTrendTopic] = useState('');
  const [trendPlatform, setTrendPlatform] = useState('Instagram');

  const [emailType, setEmailType] = useState('Cold Outreach');
  const [recipientType, setRecipientType] = useState('Customer');
  const [subject, setSubject] = useState('');
  const [keyPoints, setKeyPoints] = useState('');
  const [emailTone, setEmailTone] = useState('Professional');

  const [adPlatform, setAdPlatform] = useState('Facebook');
  const [objective, setObjective] = useState('Leads');
  const [product, setProduct] = useState('');
  const [adBudget, setAdBudget] = useState('');
  const [targetAudience, setTargetAudience] = useState('');

  const [contentType, setContentType] = useState('Blog Post');
  const [keyword, setKeyword] = useState('');
  const [topic, setTopic] = useState('');
  const [wordCount, setWordCount] = useState('800');

  const [messageType, setMessageType] = useState('Promotional');
  const [occasion, setOccasion] = useState('');
  const [offer, setOffer] = useState('');
  const [targetGroup, setTargetGroup] = useState('All Customers');

  const [challenge, setChallenge] = useState('');
  const [goal, setGoal] = useState('');
  const [timeframe, setTimeframe] = useState('3 Months');
  const [strategyBudget, setStrategyBudget] = useState('10K-50K');

  const [visitorBehavior, setVisitorBehavior] = useState('');
  const [pageViewed, setPageViewed] = useState('');
  const [timeSpent, setTimeSpent] = useState('1-3 min');
  const [deviceType, setDeviceType] = useState('Mobile');

  const [storyDuration, setStoryDuration] = useState('90 Days');
  const [storyTheme, setStoryTheme] = useState('');
  const [targetEmotion, setTargetEmotion] = useState('Trust');

  const [originalContent, setOriginalContent] = useState('');
  const [recycleContentType, setRecycleContentType] = useState('Blog Post');

  const [sampleContent, setSampleContent] = useState('');
  const [desiredPersonality, setDesiredPersonality] = useState('Professional');

  const [reviewerName, setReviewerName] = useState('');
  const [rating, setRating] = useState('⭐⭐⭐⭐');
  const [reviewPlatform, setReviewPlatform] = useState('Google');
  const [reviewText, setReviewText] = useState('');

  const [resLeadName, setResLeadName] = useState('');
  const [lastContactDate, setLastContactDate] = useState('3 Months');
  const [originalInterest, setOriginalInterest] = useState('');
  const [whyWentCold, setWhyWentCold] = useState('No Response');

  const [targetCountry, setTargetCountry] = useState('UAE/Dubai');
  const [nriContentType, setNriContentType] = useState('Social Post');
  const [nriOffer, setNriOffer] = useState('');

  const [clientName, setClientName] = useState('');
  const [caseChallenge, setCaseChallenge] = useState('');
  const [solution, setSolution] = useState('');
  const [results, setResults] = useState('');

  const [festival, setFestival] = useState('Diwali');
  const [daysUntil, setDaysUntil] = useState(30);
  const [festiveBudget, setFestiveBudget] = useState('<₹5K');
  const [specialOffer, setSpecialOffer] = useState('');

  const [customerType, setCustomerType] = useState('First-time Buyer');
  const [currentDropoffs, setCurrentDropoffs] = useState('');
  const [conversionGoal, setConversionGoal] = useState('');

  async function runAgent() {
    setLoading(true);
    setResult(null);
    setError('');
    try {
      const payload: Record<string, any> = {};
      let endpoint = '';
      if (activeAgent === 'lead-score') {
        endpoint = '/agent-ops/lead-score';
        Object.assign(payload, { leadName, mobile, source, message, budget, location, interest });
      }
      if (activeAgent === 'competitor-analysis') {
        endpoint = '/agent-ops/competitor-analysis';
        Object.assign(payload, { competitorName, competitorInfo, ourStrengths });
      }
      if (activeAgent === 'trend-hijack') {
        endpoint = '/agent-ops/trend-hijack';
        Object.assign(payload, { trendTopic, trendPlatform });
      }
      if (activeAgent === 'email-writer') {
        endpoint = '/agent-ops/email-writer';
        Object.assign(payload, { emailType, recipientType, subject, keyPoints, tone: emailTone });
      }
      if (activeAgent === 'ad-copy') {
        endpoint = '/agent-ops/ad-copy';
        Object.assign(payload, { platform: adPlatform, objective, product, budget: adBudget, targetAudience });
      }
      if (activeAgent === 'seo-content') {
        endpoint = '/agent-ops/seo-content';
        Object.assign(payload, { contentType, keyword, topic, wordCount: Number(wordCount) });
      }
      if (activeAgent === 'whatsapp-gen') {
        endpoint = '/agent-ops/whatsapp-gen';
        Object.assign(payload, { messageType, occasion, offer, targetGroup });
      }
      if (activeAgent === 'strategy') {
        endpoint = '/agent-ops/strategy';
        Object.assign(payload, { challenge, goal, timeframe, budget: strategyBudget });
      }
      if (activeAgent === 'sleep-mode') {
        endpoint = '/agent-ops/sleep-mode';
        Object.assign(payload, { visitorBehavior, pageViewed, timeSpent, deviceType });
      }
      if (activeAgent === 'story-arc') {
        endpoint = '/agent-ops/story-arc';
        Object.assign(payload, { duration: storyDuration, theme: storyTheme, targetEmotion });
      }
      if (activeAgent === 'content-recycler') {
        endpoint = '/agent-ops/content-recycler';
        Object.assign(payload, { originalContent, contentType: recycleContentType });
      }
      if (activeAgent === 'brand-voice') {
        endpoint = '/agent-ops/brand-voice';
        Object.assign(payload, { sampleContent, desiredPersonality });
      }
      if (activeAgent === 'review-response') {
        endpoint = '/agent-ops/review-response';
        Object.assign(payload, { reviewText, rating, platform: reviewPlatform, reviewerName });
      }
      if (activeAgent === 'lead-resurrection') {
        endpoint = '/agent-ops/lead-resurrection';
        Object.assign(payload, { leadName: resLeadName, lastContactDate, originalInterest, whyWentCold });
      }
      if (activeAgent === 'nri-content') {
        endpoint = '/agent-ops/nri-content';
        Object.assign(payload, { targetCountry, contentType: nriContentType, offer: nriOffer });
      }
      if (activeAgent === 'case-study') {
        endpoint = '/agent-ops/case-study';
        Object.assign(payload, { clientName, challenge: caseChallenge, solution, results });
      }
      if (activeAgent === 'festive-campaign') {
        endpoint = '/agent-ops/festive-campaign';
        Object.assign(payload, { festival, daysUntil, budget: festiveBudget, offer: specialOffer });
      }
      if (activeAgent === 'journey-map') {
        endpoint = '/agent-ops/journey-map';
        Object.assign(payload, { customerType, currentDropoffs, conversionGoal });
      }
      const response = await api.post(endpoint, payload);
      setResult(response.data);
      setShowComplete(true);
    } catch {
      setError('Agent run failed. Please try again.');
    } finally {
      setLoading(false);
    }
  }
  const runAgentRef = useRef(runAgent);
  runAgentRef.current = runAgent;

  const onInputEnter = (e: React.KeyboardEvent<HTMLElement>) => {
    if (e.key === 'Enter' && !loading) {
      e.preventDefault();
      e.stopPropagation();
      void runAgent();
    }
  };

  const onTextareaCtrlEnter = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && e.ctrlKey && !loading) {
      e.preventDefault();
      e.stopPropagation();
      void runAgent();
    }
  };

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Enter' && !e.shiftKey && !loading) {
        const target = e.target as HTMLElement;
        if (target.tagName === 'TEXTAREA') return;
        void runAgentRef.current();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [loading, activeAgent]);

  const activeAgentName = agents.find((a) => a.key === activeAgent)?.name || 'ODC AI';

  return (
    <div className="odc-page">
      <div className="odc-agent-ops-stack grid gap-6 lg:grid-cols-[240px_1fr]">
        <aside className="odc-card !p-4">
          <p className="text-lg font-bold" style={{ color: 'var(--text)' }}>
            🤖 AI Agents
          </p>
          <p className="mb-4 text-xs" style={{ color: 'var(--muted)' }}>
            ODC AI Powered
          </p>
          <div className="space-y-2">
            {agents.map((a) => (
              <button
                key={a.key}
                type="button"
                className="w-full rounded-xl border px-3 py-2 text-left transition"
                style={
                  activeAgent === a.key
                    ? { borderColor: '#BE5103', background: 'rgba(190,81,3,0.14)' }
                    : { borderColor: 'var(--border)', background: 'var(--card)' }
                }
                onClick={() => {
                  setActiveAgent(a.key);
                  setResult(null);
                  setError('');
                }}
              >
                <p className="text-sm font-bold" style={{ color: 'var(--text)' }}>
                  {a.icon} {a.name}
                </p>
                <p className="truncate text-xs" style={{ color: 'var(--muted)' }}>
                  {a.desc}
                </p>
              </button>
            ))}
          </div>
        </aside>

        <main className="odc-card !p-6">
          {activeAgent === 'lead-score' ? (
            <div className="space-y-4">
              <h2 className="text-2xl font-bold">🎯 Lead Scoring AI</h2>
              <p style={{ color: 'var(--muted)' }}>Score and qualify every lead on a 0-100 scale</p>
              <input className="odc-input" placeholder="Lead Name" value={leadName} onChange={(e) => setLeadName(e.target.value)} onKeyDown={onInputEnter} />
              <p style={{ fontSize: 11, color: 'var(--muted)', marginTop: 4 }}>Press Enter to continue</p>
              <input className="odc-input" placeholder="Mobile Number" value={mobile} onChange={(e) => setMobile(e.target.value)} onKeyDown={onInputEnter} />
              <PillGroup options={['WhatsApp', 'Facebook', 'Google', 'Walk-in', 'Referral', 'Instagram']} value={source} onChange={setSource} />
              <textarea className="odc-input" rows={3} placeholder="Message/Query" value={message} onChange={(e) => setMessage(e.target.value)} onKeyDown={onTextareaCtrlEnter} />
              <PillGroup options={['<5L', '5-10L', '10-25L', '25L+', 'Not mentioned']} value={budget} onChange={setBudget} />
              <input className="odc-input" placeholder="Location" value={location} onChange={(e) => setLocation(e.target.value)} onKeyDown={onInputEnter} />
              <input className="odc-input" placeholder="Interest" value={interest} onChange={(e) => setInterest(e.target.value)} onKeyDown={onInputEnter} />
            </div>
          ) : null}

          {activeAgent === 'competitor-analysis' ? (
            <div className="space-y-4">
              <h2 className="text-2xl font-bold">🕵️ Competitor Analysis</h2>
              <input className="odc-input" placeholder="Competitor Name" value={competitorName} onChange={(e) => setCompetitorName(e.target.value)} onKeyDown={onInputEnter} />
              <textarea className="odc-input" rows={4} placeholder="Known Info" value={competitorInfo} onChange={(e) => setCompetitorInfo(e.target.value)} onKeyDown={onTextareaCtrlEnter} />
              <textarea className="odc-input" rows={4} placeholder="Our Strengths" value={ourStrengths} onChange={(e) => setOurStrengths(e.target.value)} onKeyDown={onTextareaCtrlEnter} />
            </div>
          ) : null}

          {activeAgent === 'trend-hijack' ? (
            <div className="space-y-4">
              <h2 className="text-2xl font-bold">🔥 Trend Hijacker</h2>
              <input className="odc-input" placeholder="e.g. IPL 2026, Budget 2026, Holi" value={trendTopic} onChange={(e) => setTrendTopic(e.target.value)} onKeyDown={onInputEnter} />
              <PillGroup options={['Instagram', 'Facebook', 'Twitter', 'All']} value={trendPlatform} onChange={setTrendPlatform} />
            </div>
          ) : null}

          {activeAgent === 'email-writer' ? (
            <div className="space-y-4">
              <h2 className="text-2xl font-bold">📧 Email Writer</h2>
              <PillGroup options={['Cold Outreach', 'Follow-up', 'Newsletter', 'Promotional', 'Welcome']} value={emailType} onChange={setEmailType} />
              <PillGroup options={['Customer', 'Lead', 'Partner', 'Investor']} value={recipientType} onChange={setRecipientType} />
              <input className="odc-input" placeholder="Subject Hint" value={subject} onChange={(e) => setSubject(e.target.value)} onKeyDown={onInputEnter} />
              <textarea className="odc-input" rows={4} placeholder="Key Points" value={keyPoints} onChange={(e) => setKeyPoints(e.target.value)} onKeyDown={onTextareaCtrlEnter} />
              <PillGroup options={['Professional', 'Friendly', 'Urgent']} value={emailTone} onChange={setEmailTone} />
            </div>
          ) : null}

          {activeAgent === 'ad-copy' ? (
            <div className="space-y-4">
              <h2 className="text-2xl font-bold">📊 Ad Copy Generator</h2>
              <PillGroup options={['Facebook', 'Instagram', 'Google', 'Both']} value={adPlatform} onChange={setAdPlatform} />
              <PillGroup options={['Leads', 'Sales', 'Awareness', 'Traffic']} value={objective} onChange={setObjective} />
              <input className="odc-input" placeholder="Product/Service" value={product} onChange={(e) => setProduct(e.target.value)} onKeyDown={onInputEnter} />
              <input className="odc-input" placeholder="e.g. ₹5,000/day" value={adBudget} onChange={(e) => setAdBudget(e.target.value)} onKeyDown={onInputEnter} />
              <input className="odc-input" placeholder="Target Audience" value={targetAudience} onChange={(e) => setTargetAudience(e.target.value)} onKeyDown={onInputEnter} />
            </div>
          ) : null}

          {activeAgent === 'seo-content' ? (
            <div className="space-y-4">
              <h2 className="text-2xl font-bold">🔍 SEO Content Writer</h2>
              <PillGroup options={['Blog Post', 'Landing Page', 'GMB Post', 'FAQ Page']} value={contentType} onChange={setContentType} />
              <input className="odc-input" placeholder="e.g. plots in moradabad" value={keyword} onChange={(e) => setKeyword(e.target.value)} onKeyDown={onInputEnter} />
              <input className="odc-input" placeholder="Topic" value={topic} onChange={(e) => setTopic(e.target.value)} onKeyDown={onInputEnter} />
              <PillGroup options={['500', '800', '1200', '2000']} value={wordCount} onChange={setWordCount} />
            </div>
          ) : null}

          {activeAgent === 'whatsapp-gen' ? (
            <div className="space-y-4">
              <h2 className="text-2xl font-bold">💬 WhatsApp Generator</h2>
              <PillGroup options={['Promotional', 'Festival', 'Follow-up', 'Announcement', 'Reminder']} value={messageType} onChange={setMessageType} />
              <input className="odc-input" placeholder="e.g. Diwali, New Year, Sale" value={occasion} onChange={(e) => setOccasion(e.target.value)} onKeyDown={onInputEnter} />
              <input className="odc-input" placeholder="e.g. 50% off, Free site visit" value={offer} onChange={(e) => setOffer(e.target.value)} onKeyDown={onInputEnter} />
              <PillGroup options={['All Customers', 'New Leads', 'Old Leads', 'VIP', 'Inactive']} value={targetGroup} onChange={setTargetGroup} />
            </div>
          ) : null}

          {activeAgent === 'strategy' ? (
            <div className="space-y-4">
              <h2 className="text-2xl font-bold">🧠 Strategy Advisor</h2>
              <textarea className="odc-input" rows={4} placeholder="Describe your business challenge..." value={challenge} onChange={(e) => setChallenge(e.target.value)} onKeyDown={onTextareaCtrlEnter} />
              <input className="odc-input" placeholder="Goal" value={goal} onChange={(e) => setGoal(e.target.value)} onKeyDown={onInputEnter} />
              <PillGroup options={['1 Month', '3 Months', '6 Months', '1 Year']} value={timeframe} onChange={setTimeframe} />
              <PillGroup options={['<10K', '10K-50K', '50K-1L', '1L+']} value={strategyBudget} onChange={setStrategyBudget} />
            </div>
          ) : null}

          {activeAgent === 'sleep-mode' ? (
            <div className="space-y-4">
              <h2 className="text-2xl font-bold">🌙 Sleep Mode Agent</h2>
              <input className="odc-input" placeholder="e.g. Viewed pricing page at 11 PM" value={visitorBehavior} onChange={(e) => setVisitorBehavior(e.target.value)} onKeyDown={onInputEnter} />
              <input className="odc-input" placeholder="Page Viewed" value={pageViewed} onChange={(e) => setPageViewed(e.target.value)} onKeyDown={onInputEnter} />
              <PillGroup options={['<1 min', '1-3 min', '3-5 min', '5+ min']} value={timeSpent} onChange={setTimeSpent} />
              <PillGroup options={['Mobile', 'Desktop', 'Tablet']} value={deviceType} onChange={setDeviceType} />
            </div>
          ) : null}

          {activeAgent === 'story-arc' ? (
            <div className="space-y-4">
              <h2 className="text-2xl font-bold">📖 Story Arc Agent</h2>
              <PillGroup options={['30 Days', '60 Days', '90 Days', '6 Months']} value={storyDuration} onChange={setStoryDuration} />
              <input className="odc-input" placeholder="e.g. Trust & Growth" value={storyTheme} onChange={(e) => setStoryTheme(e.target.value)} onKeyDown={onInputEnter} />
              <PillGroup options={['Trust', 'Aspiration', 'Urgency', 'FOMO', 'Pride']} value={targetEmotion} onChange={setTargetEmotion} />
            </div>
          ) : null}

          {activeAgent === 'content-recycler' ? (
            <div className="space-y-4">
              <h2 className="text-2xl font-bold">♻️ Content Recycler</h2>
              <textarea className="odc-input" rows={8} placeholder="Paste your blog post, article, or any content here..." value={originalContent} onChange={(e) => setOriginalContent(e.target.value)} onKeyDown={onTextareaCtrlEnter} />
              <PillGroup options={['Blog Post', 'Video Script', 'Speech', 'Interview', 'Report']} value={recycleContentType} onChange={setRecycleContentType} />
            </div>
          ) : null}

          {activeAgent === 'brand-voice' ? (
            <div className="space-y-4">
              <h2 className="text-2xl font-bold">🎤 Brand Voice</h2>
              <textarea className="odc-input" rows={6} placeholder="Paste 2-3 examples of your current content..." value={sampleContent} onChange={(e) => setSampleContent(e.target.value)} onKeyDown={onTextareaCtrlEnter} />
              <PillGroup options={['Professional', 'Friendly', 'Bold', 'Inspirational', 'Authoritative']} value={desiredPersonality} onChange={setDesiredPersonality} />
            </div>
          ) : null}

          {activeAgent === 'review-response' ? (
            <div className="space-y-4">
              <h2 className="text-2xl font-bold">⭐ Review Response</h2>
              <input className="odc-input" placeholder="Reviewer Name" value={reviewerName} onChange={(e) => setReviewerName(e.target.value)} onKeyDown={onInputEnter} />
              <PillGroup options={['⭐', '⭐⭐', '⭐⭐⭐', '⭐⭐⭐⭐', '⭐⭐⭐⭐⭐']} value={rating} onChange={setRating} />
              <PillGroup options={['Google', 'Facebook', 'JustDial', 'Sulekha', 'App Store']} value={reviewPlatform} onChange={setReviewPlatform} />
              <textarea className="odc-input" rows={6} placeholder="Review Text" value={reviewText} onChange={(e) => setReviewText(e.target.value)} onKeyDown={onTextareaCtrlEnter} />
            </div>
          ) : null}

          {activeAgent === 'lead-resurrection' ? (
            <div className="space-y-4">
              <h2 className="text-2xl font-bold">💀 Lead Resurrection</h2>
              <input className="odc-input" placeholder="Lead Name" value={resLeadName} onChange={(e) => setResLeadName(e.target.value)} onKeyDown={onInputEnter} />
              <PillGroup options={['1 Month', '3 Months', '6 Months', '1 Year+']} value={lastContactDate} onChange={setLastContactDate} />
              <input className="odc-input" placeholder="Original Interest" value={originalInterest} onChange={(e) => setOriginalInterest(e.target.value)} onKeyDown={onInputEnter} />
              <PillGroup options={['Price', 'Timing', 'Competition', 'No Response', 'Budget']} value={whyWentCold} onChange={setWhyWentCold} />
            </div>
          ) : null}

          {activeAgent === 'nri-content' ? (
            <div className="space-y-4">
              <h2 className="text-2xl font-bold">🌍 NRI Content</h2>
              <PillGroup options={['UAE/Dubai', 'UK', 'USA/Canada', 'Australia', 'Singapore']} value={targetCountry} onChange={setTargetCountry} />
              <PillGroup options={['Social Post', 'WhatsApp', 'Email', 'Ad Copy']} value={nriContentType} onChange={setNriContentType} />
              <input className="odc-input" placeholder="Offer/Product" value={nriOffer} onChange={(e) => setNriOffer(e.target.value)} onKeyDown={onInputEnter} />
            </div>
          ) : null}

          {activeAgent === 'case-study' ? (
            <div className="space-y-4">
              <h2 className="text-2xl font-bold">📝 Case Study</h2>
              <input className="odc-input" placeholder="Client Name" value={clientName} onChange={(e) => setClientName(e.target.value)} onKeyDown={onInputEnter} />
              <textarea className="odc-input" rows={4} placeholder="Challenge" value={caseChallenge} onChange={(e) => setCaseChallenge(e.target.value)} onKeyDown={onTextareaCtrlEnter} />
              <textarea className="odc-input" rows={4} placeholder="Solution Provided" value={solution} onChange={(e) => setSolution(e.target.value)} onKeyDown={onTextareaCtrlEnter} />
              <textarea className="odc-input" rows={4} placeholder="e.g. 3x leads, 50% revenue increase..." value={results} onChange={(e) => setResults(e.target.value)} onKeyDown={onTextareaCtrlEnter} />
            </div>
          ) : null}

          {activeAgent === 'festive-campaign' ? (
            <div className="space-y-4">
              <h2 className="text-2xl font-bold">🎉 Festive Campaign</h2>
              <select className="odc-input" value={festival} onChange={(e) => setFestival(e.target.value)}>
                {['Diwali', 'Eid', 'Holi', 'Christmas', 'New Year', 'Navratri', 'Independence Day', 'Other'].map((f) => (
                  <option key={f} value={f}>
                    {f}
                  </option>
                ))}
              </select>
              <input className="odc-input" type="number" value={daysUntil} onChange={(e) => setDaysUntil(Number(e.target.value) || 0)} onKeyDown={onInputEnter} />
              <PillGroup options={['<₹5K', '₹5K-20K', '₹20K-50K', '₹50K+']} value={festiveBudget} onChange={setFestiveBudget} />
              <input className="odc-input" placeholder="Special Offer" value={specialOffer} onChange={(e) => setSpecialOffer(e.target.value)} onKeyDown={onInputEnter} />
            </div>
          ) : null}

          {activeAgent === 'journey-map' ? (
            <div className="space-y-4">
              <h2 className="text-2xl font-bold">🗺️ Journey Mapper</h2>
              <PillGroup options={['First-time Buyer', 'Repeat Customer', 'High-value Lead', 'Cold Lead']} value={customerType} onChange={setCustomerType} />
              <textarea className="odc-input" rows={5} placeholder="Where do you lose customers? e.g. After site visit, after price discussion..." value={currentDropoffs} onChange={(e) => setCurrentDropoffs(e.target.value)} onKeyDown={onTextareaCtrlEnter} />
              <input className="odc-input" placeholder="e.g. Book site visit, Purchase plot, Sign up" value={conversionGoal} onChange={(e) => setConversionGoal(e.target.value)} onKeyDown={onInputEnter} />
            </div>
          ) : null}

          <p style={{ fontSize: 11, color: 'var(--muted)', marginTop: 4 }}>
            Press Enter to continue
          </p>

          {error ? <p className="mt-4 text-sm text-red-500">{error}</p> : null}

          <button
            type="button"
            className="mt-5 w-full rounded-xl px-5 py-3 font-semibold text-black disabled:opacity-60"
            style={{ background: 'linear-gradient(135deg, #BE5103, #B7410E)' }}
            onClick={() => void runAgent()}
            disabled={loading}
          >
            {loading ? '🤖 Agent is processing your request...' : '⚡ Run Agent'}
          </button>

          {result ? (
            <ResultCard>
              {activeAgent === 'lead-score' ? (
                <div className="space-y-4">
                  {(() => {
                    const r = result.lead_score || {};
                    const score = Number(r.score || 0);
                    const scoreColor = score >= 70 ? '#10B981' : score >= 40 ? '#F59E0B' : '#EF4444';
                    const scoreGlow =
                      score >= 70 ? 'rgba(16,185,129,0.3)' : score >= 40 ? 'rgba(245,158,11,0.3)' : 'rgba(239,68,68,0.3)';
                    const grade = String(r.grade || 'N/A');
                    return (
                      <>
                        <div className="flex flex-wrap items-center gap-6">
                          <div style={{ position: 'relative', width: 140, height: 140, margin: '0 auto 20px' }}>
                            <div
                              style={{
                                position: 'absolute',
                                inset: -4,
                                borderRadius: '50%',
                                boxShadow: `0 0 30px ${scoreGlow}`,
                                border: `2px solid ${scoreColor}`,
                                opacity: 0.4,
                              }}
                            />
                            <div
                              style={{
                                width: '100%',
                                height: '100%',
                                borderRadius: '50%',
                                background: `conic-gradient(${scoreColor} ${Math.max(0, Math.min(score, 100)) * 3.6}deg, rgba(255,255,255,0.05) 0deg)`,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                boxShadow: `0 0 20px ${scoreGlow}`,
                              }}
                            >
                              <div
                                style={{
                                  width: '68%',
                                  height: '68%',
                                  borderRadius: '50%',
                                  background: 'var(--bg2)',
                                  display: 'flex',
                                  flexDirection: 'column',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  border: '1px solid rgba(255,255,255,0.05)',
                                }}
                              >
                                <span
                                  style={{
                                    fontSize: 28,
                                    fontWeight: 900,
                                    color: '#ffffff',
                                    lineHeight: 1,
                                    fontFamily: "'Space Grotesk', sans-serif",
                                    textShadow: `0 0 10px ${scoreColor}`,
                                  }}
                                >
                                  {score}
                                </span>
                                <span
                                  style={{
                                    fontSize: 10,
                                    fontWeight: 600,
                                    color: 'rgba(255,255,255,0.5)',
                                    letterSpacing: 1,
                                  }}
                                >
                                  /100
                                </span>
                              </div>
                            </div>
                          </div>
                          <div>
                            <div
                              style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: 6,
                                background: `${scoreGlow}`,
                                border: `1px solid ${scoreColor}`,
                                borderRadius: 20,
                                padding: '6px 18px',
                                fontSize: 14,
                                fontWeight: 800,
                                color: scoreColor,
                                marginBottom: 8,
                                boxShadow: `0 4px 12px ${scoreGlow}`,
                              }}
                            >
                              {score >= 70 ? '🔥' : score >= 40 ? '⚡' : '❄️'} {grade}
                            </div>
                            <br />
                            <span className="rounded-full px-2 py-1 text-xs font-bold" style={{ background: `${scoreColor}22`, color: '#ffffff' }}>
                              {r.priority || 'Priority pending'}
                            </span>
                          </div>
                        </div>
                        <div className="grid gap-2">
                          {Object.entries(r.score_breakdown || {}).map(([k, v]) => (
                            <div key={k} style={{ marginBottom: 10 }}>
                              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                                <span style={{ fontSize: 12, color: 'rgba(255,255,255,0.7)', fontWeight: 600 }}>
                                  {k.replaceAll('_', ' ')}
                                </span>
                                <span style={{ fontSize: 12, color: '#ffffff', fontWeight: 800 }}>
                                  {Number(v) || 0}/20
                                </span>
                              </div>
                              <div
                                style={{
                                  height: 6,
                                  borderRadius: 4,
                                  background: 'rgba(255,255,255,0.06)',
                                  overflow: 'hidden',
                                }}
                              >
                                <div
                                  style={{
                                    height: '100%',
                                    borderRadius: 4,
                                    background: `linear-gradient(90deg, ${scoreColor}, ${scoreColor}88)`,
                                    width: `${Math.max(0, Math.min((Number(v) || 0) / 20, 1)) * 100}%`,
                                    transition: 'width 1s cubic-bezier(0.4,0,0.2,1)',
                                    boxShadow: `0 0 8px ${scoreGlow}`,
                                  }}
                                />
                              </div>
                            </div>
                          ))}
                        </div>
                        <p>{r.analysis}</p>
                        <div className="rounded-xl border p-3" style={{ borderColor: 'rgba(190,81,3,0.4)' }}>
                          <strong>Recommended action:</strong> {r.recommended_action}
                        </div>
                        <p>
                          <strong>Best call time:</strong> {r.best_call_time}
                        </p>
                        <ol className="list-decimal pl-5">
                          {(r.talking_points || []).map((x: string, i: number) => (
                            <li key={`${x}-${i}`}>{x}</li>
                          ))}
                        </ol>
                        <ul className="list-disc pl-5">
                          {(r.objections_to_prepare || []).map((x: string, i: number) => (
                            <li key={`${x}-${i}`}>{x}</li>
                          ))}
                        </ul>
                        <textarea className="odc-input w-full" rows={4} readOnly value={r.whatsapp_message || ''} />
                      </>
                    );
                  })()}
                </div>
              ) : null}

              {activeAgent === 'competitor-analysis' ? (
                <div className="space-y-4">
                  {(() => {
                    const r = result.analysis || {};
                    return (
                      <>
                        <div className="grid gap-4 md:grid-cols-2">
                          <div>
                            <p className="font-bold">Strengths</p>
                            <ul className="list-disc pl-5">{(r.competitor_profile?.likely_strengths || []).map((x: string) => <li key={x}>{x}</li>)}</ul>
                          </div>
                          <div>
                            <p className="font-bold">Weaknesses</p>
                            <ul className="list-disc pl-5">{(r.competitor_profile?.likely_weaknesses || []).map((x: string) => <li key={x}>{x}</li>)}</ul>
                          </div>
                        </div>
                        <ul className="list-disc pl-5 text-emerald-400">{(r.our_advantages || []).map((x: string) => <li key={x}>{x}</li>)}</ul>
                        <div className="grid gap-2">
                          {(r.counter_strategies || []).map((s: any, i: number) => (
                            <div key={i} className="rounded-xl border p-3" style={{ borderColor: 'var(--border)' }}>
                              <p className="font-bold">{s.strategy}</p>
                              <p>{s.action}</p>
                              <p className="text-xs">{s.timeline}</p>
                            </div>
                          ))}
                        </div>
                        <textarea className="odc-input w-full" rows={4} readOnly value={r.battle_card || ''} />
                      </>
                    );
                  })()}
                </div>
              ) : null}

              {activeAgent === 'trend-hijack' ? (
                <div className="space-y-4">
                  {(() => {
                    const r = result.trend_content || {};
                    return (
                      <>
                        <p className="font-bold">{r.trend_angle}</p>
                        <div className="grid gap-3 md:grid-cols-3">
                          {(r.content_pieces || []).map((p: any, i: number) => (
                            <div key={i} className="rounded-xl border p-3" style={{ borderColor: 'var(--border)' }}>
                              <p className="font-bold">{p.platform}</p>
                              <p className="text-xs">{p.type}</p>
                              <p className="mt-2 text-sm">{p.caption || p.message}</p>
                              <p className="mt-2 text-xs">{(p.hashtags || []).map((h: string) => `#${h}`).join(' ')}</p>
                            </div>
                          ))}
                        </div>
                        <div className="flex gap-2">
                          <span className="odc-badge odc-badge-accent">{r.best_time_to_post}</span>
                          <span className="odc-badge odc-badge-green">{r.viral_probability}</span>
                        </div>
                      </>
                    );
                  })()}
                </div>
              ) : null}

              {activeAgent === 'email-writer' ? (
                <div className="space-y-4">
                  {(() => {
                    const r = result.email || {};
                    return (
                      <>
                        <div className="space-y-2">
                          {(r.subject_lines || []).map((s: string, i: number) => (
                            <button key={i} className="odc-btn-ghost w-full justify-between" onClick={() => copyText(s, 'Copied!')}>
                              {s}
                            </button>
                          ))}
                        </div>
                        <textarea className="odc-input w-full" rows={10} readOnly value={r.email_body || ''} />
                        <div className="flex flex-wrap gap-2 text-sm">
                          <span className="odc-badge odc-badge-accent">{r.best_send_time}</span>
                          <span className="odc-badge odc-badge-green">{r.expected_open_rate}</span>
                        </div>
                      </>
                    );
                  })()}
                </div>
              ) : null}

              {activeAgent === 'ad-copy' ? (
                <div className="space-y-4">
                  {(() => {
                    const r = result.ad_copy || {};
                    return (
                      <>
                        <div className="grid gap-3 md:grid-cols-2">
                          {(r.ads || []).map((ad: any, i: number) => (
                            <div key={i} className="rounded-xl border p-3" style={{ borderColor: 'var(--border)' }}>
                              <p className="font-bold">{ad.ad_name}</p>
                              <p>{ad.headline}</p>
                              <p className="text-sm">{ad.primary_text}</p>
                            </div>
                          ))}
                        </div>
                        <pre className="overflow-x-auto rounded-xl border p-3 text-xs" style={{ borderColor: 'var(--border)' }}>
                          {JSON.stringify(r.audience_targeting || {}, null, 2)}
                        </pre>
                        <p>{r.ab_test_suggestion}</p>
                      </>
                    );
                  })()}
                </div>
              ) : null}

              {activeAgent === 'seo-content' ? (
                <div className="space-y-4">
                  {(() => {
                    const r = result.seo_content || {};
                    return (
                      <>
                        <p className="font-bold">{r.seo_title}</p>
                        <textarea className="odc-input w-full" rows={2} readOnly value={r.meta_description || ''} />
                        <details className="rounded-xl border p-3" style={{ borderColor: 'var(--border)' }}>
                          <summary className="cursor-pointer font-bold">Outline</summary>
                          <pre className="mt-2 overflow-x-auto text-xs">{JSON.stringify(r.outline || [], null, 2)}</pre>
                        </details>
                        <textarea className="odc-input w-full" rows={12} readOnly value={r.full_content || ''} />
                        <div className="flex flex-wrap gap-2">
                          {(r.keywords_used || []).map((k: string) => (
                            <span key={k} className="rounded-full bg-emerald-500/20 px-2 py-1 text-xs text-emerald-300">
                              {k}
                            </span>
                          ))}
                        </div>
                      </>
                    );
                  })()}
                </div>
              ) : null}

              {activeAgent === 'whatsapp-gen' ? (
                <div className="space-y-4">
                  {(() => {
                    const r = result.whatsapp || {};
                    return (
                      <>
                        {(r.messages || []).map((m: any, i: number) => (
                          <div key={i} className="rounded-xl border p-3" style={{ borderColor: 'var(--border)' }}>
                            <p className="mb-2 font-bold">
                              Variant {m.variant} · {m.character_count} chars
                            </p>
                            <textarea className="odc-input w-full" rows={3} readOnly value={m.message || ''} />
                          </div>
                        ))}
                        <p>
                          <strong>Best send time:</strong> {r.best_send_time}
                        </p>
                        <textarea className="odc-input w-full" rows={3} readOnly value={r.follow_up_message || ''} />
                      </>
                    );
                  })()}
                </div>
              ) : null}

              {activeAgent === 'strategy' ? (
                <div className="space-y-4">
                  {(() => {
                    const r = result.strategy || {};
                    return (
                      <>
                        <p>{r.situation_analysis}</p>
                        <div className="grid gap-3">
                          {(r.strategic_options || []).map((o: any, i: number) => (
                            <div key={i} className="rounded-xl border p-3" style={{ borderColor: 'var(--border)' }}>
                              <p className="font-bold">{o.option}</p>
                              <p>{o.description}</p>
                              <p className="text-xs">Pros: {(o.pros || []).join(', ')}</p>
                              <p className="text-xs">Cons: {(o.cons || []).join(', ')}</p>
                            </div>
                          ))}
                        </div>
                        <div className="rounded-xl border p-3" style={{ borderColor: 'rgba(190,81,3,0.4)' }}>
                          <strong>Recommended:</strong> {r.recommended_strategy}
                        </div>
                        <pre className="overflow-x-auto rounded-xl border p-3 text-xs" style={{ borderColor: 'var(--border)' }}>
                          {JSON.stringify(r.action_plan || [], null, 2)}
                        </pre>
                        <p>KPIs: {(r.kpis || []).join(', ')}</p>
                        <p>Quick wins: {(r.quick_wins || []).join(', ')}</p>
                      </>
                    );
                  })()}
                </div>
              ) : null}

              {['sleep-mode', 'story-arc', 'content-recycler', 'brand-voice', 'review-response', 'lead-resurrection', 'nri-content', 'case-study', 'festive-campaign', 'journey-map'].includes(activeAgent) ? (
                <div className="space-y-3">
                  {Object.entries(result || {}).map(([section, data]) => (
                    <div key={section} className="rounded-xl border p-3" style={{ borderColor: 'var(--border)' }}>
                      <p className="mb-2 font-bold capitalize">{section.replaceAll('_', ' ')}</p>
                      <pre className="overflow-x-auto text-xs">{JSON.stringify(data, null, 2)}</pre>
                    </div>
                  ))}
                </div>
              ) : null}

              <div className="mt-6 flex flex-wrap gap-2">
                <button type="button" className="odc-btn-ghost" onClick={() => setResult(null)}>
                  🔄 Run Again
                </button>
                <button type="button" className="odc-btn-primary" onClick={() => copyText(JSON.stringify(result, null, 2), 'Copied!')}>
                  📋 Copy Result
                </button>
              </div>
            </ResultCard>
          ) : null}
        </main>
      </div>
      <AIProcessing isVisible={loading} agentName={activeAgentName} />
      <AIComplete
        isVisible={showComplete}
        agentName={activeAgentName}
        message="Your results are ready! Scroll down to view 👇"
        onClose={() => setShowComplete(false)}
      />
    </div>
  );
}
